import * as React from "react";
import {
  BooleanField,
  DateField,
  FunctionField,
  RecordContextProvider,
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useGetManyReference,
  usePermissions,
  useRecordContext,
} from "react-admin";
import { Box, Typography } from "@mui/material";
import { isSuperAdmin } from "../../auth-provider";
import { ResultShow } from "../projects/result-show";
import { EmployeeGDPRErasureButton } from "./employee-gdpr-erasure-button";

const STAGE = process.env.REACT_APP_STAGE || "staging";
const EMPLOYEE_ENDPOINT =
  STAGE === "production"
    ? "https://app.thrivetech.co/employees"
    : "https://app.chasana.biz/employees";

const EmployeeShowTitle = () => {
  const record = useRecordContext();
  return <span>Employee {record ? `"${record.name} ${record.lastName}"` : ""}</span>;
};

const EmployeeShowActions = (props) => (
  <TopToolbar {...props}>
    <EmployeeGDPRErasureButton size="medium" />
  </TopToolbar>
);

const EmployeeScoresTab = ({ employee }) => {
  const projectId = employee?.projectId;

  const {
    data: resultsData = [],
    isLoading,
    error,
  } = useGetManyReference(
    "results",
    {
      target: "projectIds",
      id: projectId,
      pagination: { page: 1, perPage: 200 },
      sort: { field: "updated", order: "DESC" },
      filter: {
        includeDeleted: true,
      },
    },
    {
      enabled: !!employee?.id && !!projectId,
      retry: false,
    }
  );

  const employeeResults = React.useMemo(
    () => (resultsData || []).filter((result) => result?.employeeId === employee?.id),
    [employee?.id, resultsData]
  );

  return (
    <Box>
      {!projectId ? <span>This employee is not assigned to a project.</span> : null}
      {projectId && isLoading ? <span>Loading employee scores...</span> : null}
      {projectId && error ? <span>Error loading employee scores.</span> : null}
      {projectId && !isLoading && !error && employeeResults.length === 0 ? (
        <span>No employee scores found for this employee.</span>
      ) : null}

      {projectId && !isLoading && !error && employeeResults.length > 0
        ? employeeResults.map((result) => (
            <Box
              key={result.id}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                mb: 2,
                px: 1,
                py: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ px: 1, pt: 0.5 }}>
                Overall Competency Score: {result?.overallCompetencyScore ?? "-"}
              </Typography>
              <RecordContextProvider value={result}>
                <ResultShow />
              </RecordContextProvider>
            </Box>
          ))
        : null}
    </Box>
  );
};

const EmployeeScoresTabContent = () => {
  const employee = useRecordContext();
  return <EmployeeScoresTab employee={employee} />;
};

export const EmployeeShow = ({ ...props }) => {
  const permissions = usePermissions();

  return (
    <Show
      {...props}
      title={<EmployeeShowTitle />}
      actions={isSuperAdmin(permissions.permissions) ? <EmployeeShowActions /> : null}
    >
      <TabbedShowLayout>
        <Tab label="Summary">
          <TextField source="id" />
          <TextField source="name" />
          <TextField source="lastName" />
          <TextField source="email" />
          <FunctionField
            label="Employee Link"
            render={(record) =>
              record?.token ? (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${EMPLOYEE_ENDPOINT}/${record.id}?token=${record.token}`}
                >
                  Employee Link
                </a>
              ) : (
                "-"
              )
            }
          />
          <TextField source="projectId" />
          <TextField source="teamId" />
          <TextField source="employeeProfileId" />
          <TextField source="externalCorrelationId" />
          <TextField source="resultStatus" />
          <TextField source="aonId" />
          <TextField source="note" />
          <DateField source="startDateTime" />
          <DateField source="completedDateTime" />
          <DateField source="created" />
          <TextField source="createdBy" />
          <DateField source="updated" />
          <TextField source="updatedBy" />
          <BooleanField source="deleted" />
          <BooleanField source="gdprDeleted" label="GDPR Deleted" />
        </Tab>
        <Tab label="SCORES">
          <EmployeeScoresTabContent />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};
